import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { FAILURE_REASONS, PURPOSES, useStartBatch } from '@/api/batches'
import type { BatchBody } from '@/api/batches'
import { useIdempotencyKey } from '@/api/idempotency'
import { useMachines } from '@/api/machines'
import type { Machine } from '@/api/machines'
import { useModelVersions } from '@/api/model-versions'
import type { ModelVersion } from '@/api/model-versions'
import { useModels } from '@/api/models'
import { poolLabel, usePools } from '@/api/pools'
import type { Pool } from '@/api/pools'
import { useProducts } from '@/api/products'
import type { Product } from '@/api/products'
import { useVariants } from '@/api/variants'
import type { Variant } from '@/api/variants'
import { useWorkspace } from '@/api/workspace'
import { formatGrams, formatMinutes, selectClass } from '@/components/batches/batch-format'
import { FieldError } from '@/components/batches/batch-parts'
import { batchErrorMessage } from '@/components/batches/error-message'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { ProductCombobox, SearchCombobox } from '@/components/search-combobox'
import { needsBackdateReason } from '@/components/restocks/error-message'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

// Start Batch (UI-SCREENS F-05, #128), phone first: native selects (searchable Product and Variant), 44px targets, numeric keyboard.
// Offers idle, non-archived Machines; the current version of a non-archived Model of a
// non-archived Product (#126 Q2), when it has a .3mf / .stl (usable for Batches, 03f #104); an optional Variant prefill; purpose; plates; a non-archived
// filament Pool per slot of the version's Recipe; the business date. Shows est. grams per slot and
// est. time (plates × Recipe, snapshotted on start, #126 Q4). The server re-checks all of it.
//
// "Record completed Batch" mode (`?mode=record`, 04d #130): the same form for a print that already
// finished, with the F-07 results (OK and failed parts, failure reason, actual grams per slot,
// actual minutes typed in) and a backdated date. Any non-archived Machine (it may be printing) and
// any non-archived version of the Model (#126 Q2); saving posts the movements at once.
const searchSchema = z.object({
  machineId: z.coerce.number().int().positive().optional().catch(undefined),
  mode: z.enum(['record']).optional().catch(undefined),
})

export const Route = createFileRoute('/w/$slug/batches/new')({
  component: NewBatch,
  validateSearch: searchSchema,
  staticData: { breadcrumb: 'batches.form.title' },
})

function NewBatch() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const { machineId, mode } = Route.useSearch()
  const record = mode === 'record'
  const workspace = useWorkspace(slug)
  const machines = useMachines(slug)
  const products = useProducts(slug)
  const pools = usePools(slug)

  const queries = [workspace, machines, products, pools]
  return (
    <section className="flex max-w-2xl flex-col gap-4">
      <Link to="/w/$slug/batches" params={{ slug }} className="text-sm underline-offset-4 hover:underline">
        {t('batches.back')}
      </Link>
      <h1 className="font-heading text-xl font-semibold">{t(record ? 'batches.record.title' : 'batches.form.title')}</h1>
      <div className="grid grid-cols-2 gap-2">
        <Button asChild variant={record ? 'outline' : 'default'} className="h-11">
          <Link to="/w/$slug/batches/new" params={{ slug }} search={{ machineId }}>
            {t('batches.start')}
          </Link>
        </Button>
        <Button asChild variant={record ? 'default' : 'outline'} className="h-11">
          <Link to="/w/$slug/batches/new" params={{ slug }} search={{ machineId, mode: 'record' }}>
            {t('batches.record.title')}
          </Link>
        </Button>
      </div>
      {record ? <p className="text-sm text-muted-foreground">{t('batches.record.description')}</p> : null}

      {queries.some((query) => query.isPending) ? (
        <LoadingSkeleton variant="form" rows={4} />
      ) : !workspace.data || !machines.data || !products.data || !pools.data ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : !workspace.data.adopted_on ? (
        <Card>
          <CardContent className="py-4">
            <p role="status" className="text-sm">
              {t('restocks.errors.adoptionDateNotSet')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <BatchForm
          key={record ? 'record' : 'start'}
          record={record}
          slug={slug}
          adoptedOn={workspace.data.adopted_on}
          timezone={workspace.data.timezone}
          machines={machines.data.filter((machine) => !machine.archived_at && (record || machine.status === 'idle'))}
          products={products.data.filter((product) => !product.archived_at)}
          pools={pools.data.filter((pool) => pool.kind === 'filament' && !pool.archived_at)}
          initialMachineId={machineId}
        />
      )}
    </section>
  )
}

// Today in the Workspace's timezone: the business date may never be after it (domain-rules).
function todayIn(timezone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date())
}

// RHF would read a numeric key as an array index, so slot Pools are keyed "s<model_slot_id>".
const slotKey = (modelSlotId: number) => `s${modelSlotId}`

// The whole number of plates, or 0 while the field isn't one: the estimates show "—" then.
function platesOf(value: string | undefined): number {
  return value && /^\d+$/.test(value) ? Number(value) : 0
}

const isWhole = (value: string | undefined) => value !== undefined && /^\d+$/.test(value.trim())
const toWhole = (value: string | undefined) => (isWhole(value) ? Number(value) : null)

function BatchForm({
  record,
  slug,
  adoptedOn,
  timezone,
  machines,
  products,
  pools,
  initialMachineId,
}: {
  record: boolean
  slug: string
  adoptedOn: string
  timezone: string
  machines: Machine[]
  products: Product[]
  pools: Pool[]
  initialMachineId?: number
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const start = useStartBatch()
  const idempotency = useIdempotencyKey()
  const [askReason, setAskReason] = useState(false)
  const [failedTouched, setFailedTouched] = useState(false)
  const today = todayIn(timezone)
  const required = t('batches.form.required')
  const wholeNumber = t('batches.complete.wholeNumber')

  // The results are checked in "Record completed Batch" mode only (as F-07 checks them).
  const formSchema = z
    .object({
      machineId: z.string().min(1, { message: required }),
      productId: z.string().min(1, { message: required }),
      modelId: z.string().min(1, { message: required }),
      versionId: z.string(),
      variantId: z.string(),
      purpose: z.string().min(1, { message: required }),
      plates: z.string().refine((value) => /^\d+$/.test(value) && Number(value) > 0, { message: t('batches.form.platesInvalid') }),
      occurredOn: z.string().min(1, { message: required }),
      backdateReason: z.string(),
      slotPools: z.record(z.string(), z.string()),
      partsOk: z.string(),
      partsFailed: z.string(),
      failureReason: z.string(),
      actualMinutes: z.string(),
      grams: z.record(z.string(), z.string()),
    })
    .superRefine((values, context) => {
      if (!record) return
      const ok = toWhole(values.partsOk)
      const failed = toWhole(values.partsFailed)
      if (ok === null) context.addIssue({ code: 'custom', path: ['partsOk'], message: wholeNumber })
      if (failed === null) context.addIssue({ code: 'custom', path: ['partsFailed'], message: wholeNumber })
      if (ok !== null && failed !== null && ok + failed === 0) {
        context.addIssue({ code: 'custom', path: ['partsOk'], message: t('batches.complete.nothingPrinted') })
      }
      if (failed !== null && failed > 0 && values.failureReason === '') {
        context.addIssue({ code: 'custom', path: ['failureReason'], message: required })
      }
      const minutes = toWhole(values.actualMinutes)
      if (minutes === null || minutes === 0) {
        context.addIssue({ code: 'custom', path: ['actualMinutes'], message: t('batches.complete.positiveNumber') })
      }
      for (const slot of version?.slots ?? []) {
        const key = slotKey(slot.model_slot_id)
        if (!isWhole(values.grams[key])) context.addIssue({ code: 'custom', path: ['grams', key], message: wholeNumber })
      }
    })
  type FormValues = z.infer<typeof formSchema>

  const initialMachine = machines.some((machine) => machine.id === initialMachineId) ? String(initialMachineId) : ''
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      machineId: initialMachine,
      productId: '',
      modelId: '',
      variantId: '',
      purpose: 'production',
      plates: '1',
      occurredOn: today,
      backdateReason: '',
      slotPools: {},
      versionId: '',
      partsOk: '',
      partsFailed: '',
      failureReason: '',
      actualMinutes: '',
      grams: {},
    },
  })
  const [productId, modelId, versionId, variantId, plates, slotPools, partsFailed, purpose] = useWatch({
    control: form.control,
    name: ['productId', 'modelId', 'versionId', 'variantId', 'plates', 'slotPools', 'partsFailed', 'purpose'],
  })

  const models = useModels(slug, Number(productId), { enabled: productId !== '' })
  const versions = useModelVersions(slug, Number(modelId), { enabled: modelId !== '' })
  const variants = useVariants(slug, Number(productId), { enabled: productId !== '' })
  const activeModels = (models.data ?? []).filter((model) => !model.archived_at)
  const activeVariants = (variants.data ?? []).filter((variant) => !variant.archived_at)
  const currentVersion: ModelVersion | undefined = (versions.data ?? []).find((row) => row.current)
  // A version is usable for Batches only with a .3mf / .stl attached (03f #104); the server
  // refuses the others too.
  const current = currentVersion?.usable_for_batches ? currentVersion : undefined
  // "Record completed Batch" may print any non-archived, usable version (#126 Q2). '' is the newest
  // one: the current version, or the newest usable one should the current one not be, so the form
  // is never left without a version.
  const usableVersions = (versions.data ?? [])
    .filter((row) => !row.archived_at && row.usable_for_batches)
    .sort((a, b) => b.version_no - a.version_no)
  const newest = current ?? usableVersions[0]
  const olderVersions = usableVersions.filter((row) => row.id !== newest?.id)
  const version: ModelVersion | undefined = !record
    ? current
    : versionId === ''
      ? newest
      : olderVersions.find((row) => String(row.id) === versionId)
  const variant: Variant | undefined = activeVariants.find((row) => String(row.id) === variantId)
  const archivedSlots = (version?.slots ?? []).filter((slot) => slot.slot_archived_at)
  const needsColor = variant
    ? (version?.slots ?? []).filter((slot) =>
        variant.slots.some((row) => row.model_slot_id === slot.model_slot_id && row.needs_color) ||
        !variant.slots.some((row) => row.model_slot_id === slot.model_slot_id),
      )
    : []
  const plateCount = platesOf(plates)
  const expected = version && plateCount > 0 ? plateCount * version.parts_per_plate : null
  const failed = toWhole(partsFailed)

  // Failed follows expected − OK until it is typed in by hand (#126 Q8, as F-07).
  const onOkChange = (value: string) => {
    const ok = toWhole(value)
    if (!failedTouched && ok !== null && expected !== null) form.setValue('partsFailed', String(Math.max(expected - ok, 0)))
  }

  // A new Product or Model starts the rest of the form over; a Variant prefills the slot Pools
  // from its mapping (skipping unmapped or archived ones, which need color).
  const resetBelowProduct = () => {
    form.setValue('modelId', '')
    form.setValue('versionId', '')
    form.setValue('variantId', '')
    form.setValue('slotPools', {})
    form.setValue('grams', {})
  }
  // A new Model has other versions and slots: its version and the slot values start over.
  const resetBelowModel = () => {
    form.setValue('versionId', '')
    form.setValue('slotPools', {})
    form.setValue('grams', {})
  }
  const prefillFrom = (id: string) => {
    const chosen = activeVariants.find((row) => String(row.id) === id)
    if (!chosen) return
    const next: Record<string, string> = { ...form.getValues('slotPools') }
    for (const row of chosen.slots) {
      if (row.pool_id && !row.pool_archived) next[slotKey(row.model_slot_id)] = String(row.pool_id)
    }
    form.setValue('slotPools', next)
  }

  const onSubmit = form.handleSubmit((values) => {
    if (!version) return
    const reason = values.backdateReason.trim()
    const failedCount = Number(values.partsFailed)
    const results = record
      ? {
          entry_mode: 'after_the_fact',
          parts_ok: Number(values.partsOk),
          parts_failed: failedCount,
          ...(failedCount > 0 ? { failure_reason: values.failureReason } : {}),
          actual_minutes: Number(values.actualMinutes),
        }
      : {}
    const body: BatchBody = {
      batch: {
        ...results,
        machine_id: Number(values.machineId),
        model_version_id: version.id,
        variant_id: values.variantId === '' ? null : Number(values.variantId),
        purpose: values.purpose,
        plates: Number(values.plates),
        occurred_on: values.occurredOn,
        ...(reason === '' ? {} : { backdate_reason: reason }),
        slots: version.slots.map((slot) => ({
          model_slot_id: slot.model_slot_id,
          pool_id: Number(values.slotPools[slotKey(slot.model_slot_id)] ?? 0),
          ...(record ? { actual_grams: Number(values.grams[slotKey(slot.model_slot_id)]) } : {}),
        })),
      },
    }
    start.mutate(
      { params: { path: { slug }, header: idempotency.headerFor(body) }, body },
      {
        onSuccess: (data) => navigate({ to: '/w/$slug/batches/$batchId', params: { slug, batchId: String(data.id) } }),
        onSettled: (_data, error) => {
          if (needsBackdateReason(error)) setAskReason(true)
          idempotency.settle(error)
        },
      },
    )
  })

  const missingPool = (version?.slots ?? []).some((slot) => !slotPools?.[slotKey(slot.model_slot_id)])
  const blocked = !version || archivedSlots.length > 0 || needsColor.length > 0 || missingPool
  const errors = form.formState.errors

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
      <p role="alert" aria-live="assertive" className="text-sm text-destructive empty:hidden">
        {start.isError ? batchErrorMessage(t, start.error) : ''}
      </p>

      <div className="flex flex-col gap-2">
        <Label htmlFor="machineId">{t('batches.form.machine')}</Label>
        <select id="machineId" className={selectClass} {...form.register('machineId')}>
          <option value="">{t('batches.form.select')}</option>
          {machines.map((machine) => (
            <option key={machine.id} value={String(machine.id)}>
              {machine.name}
            </option>
          ))}
        </select>
        {record ? <p className="text-sm text-muted-foreground">{t('batches.record.machineHint')}</p> : null}
        {!record && machines.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('batches.form.noIdleMachine')}</p>
        ) : null}
        <FieldError message={errors.machineId?.message} />
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="productId">{t('batches.form.product')}</Label>
        <ProductCombobox
          id="productId"
          value={productId}
          products={products}
          invalid={Boolean(errors.productId)}
          onChange={(value) => {
            form.setValue('productId', value, { shouldValidate: form.formState.isSubmitted })
            resetBelowProduct()
          }}
        />
        <FieldError message={errors.productId?.message} />
      </div>

      {productId ? (
        <div className="flex flex-col gap-2">
          <Label htmlFor="modelId">{t('batches.form.model')}</Label>
          <select
            id="modelId"
            className={selectClass}
            {...form.register('modelId', { onChange: resetBelowModel })}
          >
            <option value="">{t('batches.form.select')}</option>
            {activeModels.map((model) => (
              <option key={model.id} value={String(model.id)}>
                {model.name}
              </option>
            ))}
          </select>
          <FieldError message={errors.modelId?.message} />
        </div>
      ) : null}

      {record && modelId && versions.data ? (
        newest ? (
          <div className="flex flex-col gap-2">
            <Label htmlFor="versionId">{t('batches.record.version')}</Label>
            <select id="versionId" className={selectClass} {...form.register('versionId')}>
              <option value="">
                {t(current ? 'batches.record.currentVersionOption' : 'batches.record.versionOption', {
                  version: newest.version_no,
                })}
              </option>
              {olderVersions.map((row) => (
                <option key={row.id} value={String(row.id)}>
                  {t('batches.record.versionOption', { version: row.version_no })}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <p role="alert" className="text-sm text-destructive">
            {t('batches.record.noVersion')}
          </p>
        )
      ) : !record && modelId && versions.data ? (
        version ? (
          <p className="text-sm">{t('batches.form.currentVersion', { version: version.version_no })}</p>
        ) : currentVersion ? (
          <p role="alert" className="text-sm text-destructive">
            {t('batches.form.versionNoFiles', { version: currentVersion.version_no })}
          </p>
        ) : (
          <p role="alert" className="text-sm text-destructive">
            {t('batches.form.noCurrentVersion')}
          </p>
        )
      ) : null}

      {archivedSlots.length > 0 ? (
        <p role="alert" className="text-sm text-destructive">
          {t('batches.errors.recipeSlotArchived')}
        </p>
      ) : null}

      {productId ? (
        <div className="flex flex-col gap-2">
          <Label htmlFor="variantId">{t('batches.form.variant')}</Label>
          <SearchCombobox
            id="variantId"
            value={variantId}
            options={activeVariants.map((row) => ({ value: String(row.id), code: row.sku, name: row.name }))}
            noneLabel={t('batches.form.noVariant')}
            placeholder={t('searchCombobox.variant.placeholder')}
            searchPlaceholder={t('searchCombobox.variant.search')}
            onChange={(value) => {
              form.setValue('variantId', value)
              prefillFrom(value)
            }}
          />
          <p className="text-sm text-muted-foreground">{t('batches.form.variantHint')}</p>
          {needsColor.length > 0 ? (
            <p role="alert" className="text-sm text-destructive">
              {t('batches.errors.variantNeedsColor')}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-2">
          <Label htmlFor="purpose">{t('batches.form.purpose')}</Label>
          <select id="purpose" className={selectClass} {...form.register('purpose')}>
            {PURPOSES.map((value) => (
              <option key={value} value={value}>
                {t(`batches.purpose.${value}`)}
              </option>
            ))}
          </select>
          <FieldError message={errors.purpose?.message} />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="plates">{t('batches.form.plates')}</Label>
          <Input id="plates" inputMode="numeric" className="h-11" {...form.register('plates')} />
          <FieldError message={errors.plates?.message} />
        </div>
      </div>

      {version ? (
        <div className="flex flex-col gap-3">
          <h2 className="font-heading text-base font-semibold">{t('batches.form.slots')}</h2>
          {version.slots.map((slot) => (
            <Card key={slot.model_slot_id}>
              <CardContent className="flex flex-col gap-2 py-4">
                <Label htmlFor={`slot-${slot.model_slot_id}`}>{slot.label}</Label>
                <select
                  id={`slot-${slot.model_slot_id}`}
                  className={selectClass}
                  {...form.register(`slotPools.${slotKey(slot.model_slot_id)}`)}
                >
                  <option value="">{t('batches.form.selectPool')}</option>
                  {pools.map((pool) => (
                    <option key={pool.id} value={String(pool.id)}>
                      {poolLabel(pool)}
                    </option>
                  ))}
                </select>
                <span className="text-sm text-muted-foreground">
                  {t('batches.form.estGrams')}: {plateCount > 0 ? formatGrams(plateCount * slot.est_grams_per_plate) : '—'}
                </span>
                {record ? (
                  <>
                    <Label htmlFor={`grams-${slot.model_slot_id}`}>{t('batches.record.actualGrams')}</Label>
                    <Input
                      id={`grams-${slot.model_slot_id}`}
                      inputMode="numeric"
                      className="h-11"
                      {...form.register(`grams.${slotKey(slot.model_slot_id)}`)}
                    />
                    <FieldError message={errors.grams?.[slotKey(slot.model_slot_id)]?.message} />
                  </>
                ) : null}
              </CardContent>
            </Card>
          ))}
          <p className="text-sm">
            {t('batches.form.estTime')}:{' '}
            {plateCount > 0 ? formatMinutes(t, plateCount * version.est_minutes_per_plate) : '—'}
          </p>
          <p className="text-sm text-muted-foreground">{t('batches.detail.snapshotHint')}</p>
        </div>
      ) : null}

      {record ? (
        <div className="flex flex-col gap-3">
          <h2 className="font-heading text-base font-semibold">{t('batches.record.results')}</h2>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-2">
              <Label htmlFor="partsOk">{t('batches.complete.partsOk')}</Label>
              <Input
                id="partsOk"
                inputMode="numeric"
                className="h-11"
                {...form.register('partsOk', { onChange: (event) => onOkChange(event.target.value) })}
              />
              <FieldError message={errors.partsOk?.message} />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="partsFailed">{t('batches.complete.partsFailed')}</Label>
              <Input
                id="partsFailed"
                inputMode="numeric"
                className="h-11"
                {...form.register('partsFailed', { onChange: () => setFailedTouched(true) })}
              />
              <FieldError message={errors.partsFailed?.message} />
            </div>
          </div>
          {expected !== null ? (
            <p className="text-sm text-muted-foreground">{t('batches.complete.partsFailedHint', { expected })}</p>
          ) : null}

          {failed !== null && failed > 0 ? (
            <div className="flex flex-col gap-2">
              <Label htmlFor="failureReason">{t('batches.complete.failureReason')}</Label>
              <select id="failureReason" className={selectClass} {...form.register('failureReason')}>
                <option value="">{t('batches.complete.selectReason')}</option>
                {FAILURE_REASONS.map((value) => (
                  <option key={value} value={value}>
                    {t(`batches.failureReason.${value}`)}
                  </option>
                ))}
              </select>
              <FieldError message={errors.failureReason?.message} />
            </div>
          ) : null}

          <div className="flex flex-col gap-2">
            <Label htmlFor="actualMinutes">{t('batches.complete.actualMinutes')}</Label>
            <Input id="actualMinutes" inputMode="numeric" className="h-11" {...form.register('actualMinutes')} />
            <p className="text-sm text-muted-foreground">{t('batches.record.actualMinutesHint')}</p>
            <FieldError message={errors.actualMinutes?.message} />
          </div>

          <p className="text-sm text-muted-foreground">
            {t(purpose === 'production' ? 'batches.complete.wipHint' : 'batches.complete.noWipHint')}
          </p>
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <Label htmlFor="occurredOn">{t('batches.form.date')}</Label>
        <Input id="occurredOn" type="date" className="h-11" min={adoptedOn} max={today} {...form.register('occurredOn')} />
        <p className="text-sm text-muted-foreground">{t(record ? 'batches.record.dateHint' : 'batches.form.dateHint')}</p>
        <FieldError message={errors.occurredOn?.message} />
      </div>

      {askReason ? (
        <div className="flex flex-col gap-2">
          <Label htmlFor="backdateReason">{t('restocks.form.backdateReason')}</Label>
          <Input id="backdateReason" className="h-11" {...form.register('backdateReason')} />
          <p className="text-sm text-muted-foreground">{t('restocks.form.backdateReasonHint')}</p>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button type="submit" className="h-11" disabled={start.isPending || blocked}>
          {t(start.isPending ? 'batches.form.saving' : record ? 'batches.record.save' : 'batches.form.save')}
        </Button>
        <Button asChild variant="ghost" className="h-11">
          <Link to="/w/$slug/batches" params={{ slug }}>
            {t('batches.form.cancel')}
          </Link>
        </Button>
      </div>
    </form>
  )
}
